import { gql } from "generated-graphql";

export const SUBMISSION_VIRTUAL_EVENTS_QUERY = gql(`
  query SubmissionVirtualEvents($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    submissionVirtualEvents(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        seriesId
        taskId
        frequency
        interval
        title
        epaIds
        facilities {
          id
          name
        }
        status
        deadline
        assignee {
          ...AssigneePicker
        }
        tags {
          name
        }
        submission {
            id
            tenantId
            title
            submissionType
            status
            encampStatus
            signatureType
            reviewMode
            dueDate
            submittedAt
            notes
            facilitySubmissions {
            id
            facility {
                id
                name
            }
            }
            staffAssignee {
                id
                email
                person {
                    id
                    first
                    last
                }
            }
            reviewers {
                id
                user {
                    id
                    email
                    type
                    person {
                        id
                        first
                        last
                    }
                }
                isSigner
                isStaff
                reviewOrder
                completedAt
                reviewerStatus
                signatureStatus
                submissionDocumentReviews{
                    id
                    submissionReviewerId
                    submissionDocumentId
                }
            }
            peerReviewRequired
            documents {
                id
                document {
                    title
                }
            }
            createdAt
            updatedAt
        }
        waitingOn {
          id
          email
          person {
            first
            last
          }
        }
      }
    }
  }
`);

export const CREATE_SUBMISSION_MUTATION = gql(`
  mutation CreateSubmission($input: CreateSubmissionInput!) {
    createSubmission(input: $input) {
      id
      title
      submissionType
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      peerReviewRequired
      reviewMode
      signatureType
      status
      createdAt
      updatedAt
      tenantId
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
        }
      }
      reviewers {
        id
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
      }
      documents {
        id
        type
        status
        document {
          id
          title
          documentType
          storageLink
          description
        }
      }
    }
  }
`);

export const UPDATE_SUBMISSION_MUTATION = gql(`
  mutation UpdateSubmission($input: UpdateSubmissionInput!) {
    updateSubmission(input: $input) {
      id
      title
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      peerReviewRequired
      reviewMode
      signatureType
      status
      updatedAt
      tenantId
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
        }
      }
      reviewers {
        id
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
        completedAt
        reviewerStatus
        signatureStatus
      }
      documents {
        id
        type
        status
        document {
          id
          title
          documentType
          storageLink
          description
        }
      }
    }
  }
`);

export const MOVE_TO_REVIEW_MUTATION = gql(`
  mutation MoveToReview($input: UpdateSubmissionInput!) {
    moveToReview(input: $input) {
      id
      title
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      peerReviewRequired
      reviewMode
      signatureType
      status
      updatedAt
      tenantId
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
        }
      }
      reviewers {
        id
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
        completedAt
        reviewerStatus
        signatureStatus
      }
      documents {
        id
        type
        status
        document {
          id
          title
          documentType
          storageLink
          description
        }
      }
    }
  }
`);

export const GET_SUBMISSION_QUERY = gql(`
  query GetSubmission($id: ID!) {
    submission(id: $id) {
      id
      title
      submissionType
      recipient
      submissionMethod
      dueDate
      reportingPeriodStart
      reportingPeriodEnd
      notes
      reviewRequired
      peerReviewRequired
      reviewMode
      signatureType
      status
      encampStatus
      source
      createdAt
      updatedAt
      staffAssignee {
        id
        email
        person {
          first
          last
        }
      }
      processedManifestExceptionReport {
        id
        manifestTrackingNumber
      }
      facilitySubmissions {
        id
        facility {
          id
          name
          streetAddress1
          city
          state
          customerFacilityId
          facilityAlternateIds {
            type
            value
            hasNoId
          }
        }
      }
      reviewers {
        id
        isSigner
        completedAt
        signerNameOverride
        signerEmailOverride
        streetAddress1
        streetAddress2
        city
        state
        zip
        country
        user {
          id
          email
          person {
            first
            last
          }
          facilities {
            id
          }
        }
        submissionDocumentReviews {
            id
            submissionReviewerId
            submissionReviewer {
              userId
              isSigner
            }
            submissionDocumentId
            submissionDocument {
              id
              type
              externalDocumentId
            }
            hasViewed
            createdAt
        }
        reviewOrder
        isRequired
        isSigner
        isStaff
        dueDate
        completedAt
        reviewerStatus
        signatureStatus
      }
      documents {
        id
        type
        status
        externalDocumentId
        submissionDocumentReviews {
            id
            submissionReviewerId
            submissionReviewer {
              userId
              isSigner
            }
            submissionDocumentId
            hasViewed
        }
        document {
          id
          title
          documentType
          storageLink
          description
          fileExtension
        }
      }
      comments {
        ...CommentThread
      }
      payments {
        id
        amount
        payment {
          id
          amount
          payee
          paymentMethod
          paidAt
          status
        }
      }
      issues {
        ...issue
      }
      revisedFromSubmissionId
      revisedFromSubmission {
        id
        title
        createdAt
      }
      revisionSubmissions {
        id
        title
        createdAt
      }
    }
  }
`);

export const SUBMISSION_REVISION_CHECK_QUERY = gql(`
  query SubmissionRevisionCheck($submissionIds: [ID!]!) {
    getRevisionSubmissions(submissionIds: $submissionIds) {
      id
      title
      revisionSubmissions {
        id
        title
      }
    }
  }
`);

export const DELETE_SUBMISSION_MUTATION = gql(`
  mutation DeleteSubmission($id: ID!) {
    deleteSubmission(id: $id)
  }
`);

export const BULK_DELETE_SUBMISSIONS_MUTATION = gql(`
  mutation BulkDeleteSubmissions($input: BulkDeleteSubmissionsInput!) {
    bulkDeleteSubmissions(input: $input)
  }
`);

export const APPROVE_SUBMISSION_DOCUMENT_REVIEW_MUTATION = gql(`
  mutation ApproveSubmissionDocumentReview($submissionDocumentReviewId: ID!) {
    approveSubmissionDocumentReview(
      submissionDocumentReviewId: $submissionDocumentReviewId
    ) {
      id
      submissionReviewerId
      submissionDocumentId
      submissionDocument {
        id
        submissionId
        status
      }
    }
  }
`);

export const REJECT_SUBMISSION_DOCUMENT_REVIEW_MUTATION = gql(`
  mutation RejectSubmissionDocumentReview(
    $submissionDocumentReviewId: ID!
    $rejectionComment: String
  ) {
    rejectSubmissionDocumentReview(
      submissionDocumentReviewId: $submissionDocumentReviewId
      rejectionComment: $rejectionComment
    ) {
      id
      submissionReviewerId
      submissionDocumentId
      submissionDocument {
        id
        submissionId
        status
      }
    }
  }
`);

export const SUBMISSION_FACILITIES_QUERY = gql(`
  query SubmissionFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        customerFacilityId
        streetAddress1
        city
        state
        zip
        facilityAlternateIds {
          type
          value
          hasNoId
        }
      }
      count
    }
  }
`);

export const GET_EMBEDDED_DOCUMENT_PREPARATION_URL = gql(`
  query GetEmbeddedDocumentPreparationUrl($submissionId: ID!) {
    getEmbeddedDocumentPreparationUrl(submissionId: $submissionId)
  }
`);

export const GET_EMBEDDED_DOCUMENT_SIGNING_URL = gql(`
  query GetEmbeddedDocumentSigningUrl($submissionId: ID!) {
    getEmbeddedDocumentSigningUrl(submissionId: $submissionId)
  }
`);

export const GET_VIRTUAL_EVENT_FOR_SUBMISSION_QUERY = gql(`
  query GetVirtualEventForSubmission($taskId: ID!, $deadline: DateTime!) {
    virtualEvent(taskId: $taskId, deadline: $deadline) {
      id
      taskId
      seriesId
      title
      description
      deadline
      completedAt
      reminderDays
      frequency
      interval
      facilities {
        id
        name
      }
      assignee {
        id
        email
        person {
          first
          last
        }
      }
      watchers {
        id
        email
        person {
          first
          last
        }
      }
      documents {
        id
        fileExtension
        title
        documentType
      }
      tags {
        id
        name
      }
      submission {
        id
        title
        submissionType
        recipient
        submissionMethod
        notes
      }
      links
    }
  }
`);

export const UPDATE_SUBMISSION_STATUS_MUTATION = gql(`
  mutation UpdateSubmissionStatus($input: UpdateSubmissionStatusInput!) {
    updateSubmissionStatus(input: $input) {
      id
      status
      updatedAt
    }
  }
`);

export const FORCE_COMPLETE_SUBMISSION_MUTATION = gql(`
  mutation ForceCompleteSubmission($input: ForceCompleteSubmissionInput!) {
    forceCompleteSubmission(input: $input) {
      id
      status
      updatedAt
      documents {
        id
        type
        document {
          id
          title
        }
      }
    }
  }
`);

export const SWAP_SUBMISSION_DOCUMENT_MUTATION = gql(`
  mutation SwapSubmissionDocument($input: SwapSubmissionDocumentInput!) {
    swapSubmissionDocument(input: $input) {
      id
      updatedAt
      documents {
        id
        type
        document {
          id
          title
        }
      }
    }
  }
`);

export const UPDATE_SIGNATURE_STATUS_TO_SENT_MUTATION = gql(`
  mutation UpdateSignatureStatusToSent($submissionId: ID!) {
    updateSignatureStatusToSent(submissionId: $submissionId)
  }
`);

export const BULK_SUBMISSIONS_SAFE_TO_UPDATE_QUERY = gql(`
  query BulkSubmissionsSafeToUpdate($submissionIds: [ID!]!, $targetStatus: SubmissionStatus!) {
    bulkSubmissionsSafeToUpdate(submissionIds: $submissionIds, targetStatus: $targetStatus) {
      id
      isSafe
      reasons
      submission {
        id
        title
        submissionType
        status
        dueDate
        facilitySubmissions {
          id
          facility {
            id
            name
          }
        }
      }
    }
  }
`);

export const BULK_UPDATE_SUBMISSIONS_STATUS_MUTATION = gql(`
  mutation BulkUpdateSubmissionsStatus(
    $input: BulkUpdateSubmissionsStatusInput!
  ) {
    bulkUpdateSubmissionsStatus(input: $input) {
      succeeded
      jobId
    }
  }
`);

export const SET_SUBMISSION_ASSIGNEES_MUTATION = gql(`
  mutation SetSubmissionAssignees($submissionIds: [ID!]!, $assigneeId: ID!) {
    setSubmissionAssignees(submissionIds: $submissionIds, assigneeId: $assigneeId) {
      succeeded
      updated
      failed
    }
  }
`);

export const SET_SUBMISSION_DUE_DATES_MUTATION = gql(`
  mutation SetSubmissionDueDates($submissionIds: [ID!]!, $virtualEventIds: [ID!], $dueDate: DateTime!) {
    setSubmissionDueDates(submissionIds: $submissionIds, virtualEventIds: $virtualEventIds, dueDate: $dueDate) {
      succeeded
      updated
      failed
    }
  }
`);

export const BULK_SUBMISSIONS_SAFE_TO_SEND_TO_REVIEWERS_QUERY = gql(`
  query BulkSubmissionsSafeToSendToReviewers($submissionIds: [ID!]!) {
    bulkSubmissionsSafeToSendToReviewers(submissionIds: $submissionIds) {
      id
      isSafe
      reasons
      submission {
        id
        title
        submissionType
        status
        dueDate
        facilitySubmissions {
          id
          facility {
            id
            name
          }
        }
      }
    }
  }
`);

export const SEND_SUBMISSIONS_TO_REVIEWERS_MUTATION = gql(`
  mutation SendSubmissionsToReviewers($submissionIds: [ID!]!) {
    sendSubmissionsToReviewers(submissionIds: $submissionIds) {
      succeeded
      jobId
    }
  }
`);

export const BULK_ADD_REVIEWERS_MUTATION = gql(`
  mutation BulkAddReviewers($input: BulkAddReviewersInput!) {
    bulkAddReviewers(input: $input) {
      succeeded
      updated
      failed
    }
  }
`);

export const BULK_SET_SIGNER_MUTATION = gql(`
  mutation BulkSetSigner($input: BulkSetSignerInput!) {
    bulkSetSigner(input: $input) {
      succeeded
      updated
      failed
    }
  }
`);

export const UPDATE_SUBMISSION_ENCAMP_STATUS_MUTATION = gql(`
  mutation UpdateSubmissionEncampStatus($submissionId: ID!, $encampStatus: SubmissionEncampStatus) {
    updateSubmissionEncampStatus(submissionId: $submissionId, encampStatus: $encampStatus) {
      id
      status
      encampStatus
    }
  }
`);

export const BULK_UPDATE_SUBMISSION_ENCAMP_STATUS_MUTATION = gql(`
  mutation BulkUpdateSubmissionEncampStatus($submissionIds: [ID!]!, $encampStatus: SubmissionEncampStatus) {
    bulkUpdateSubmissionEncampStatus(submissionIds: $submissionIds, encampStatus: $encampStatus) {
      succeeded
      updated
      failed
    }
  }
`);

export const RESOLVE_SUBMISSION_REJECTION_MUTATION = gql(`
  mutation ResolveSubmissionRejection($input: ResolveSubmissionRejectionInput!) {
    resolveSubmissionRejection(input: $input) {
      id
      status
      encampStatus
    }
  }
`);

export const RESET_SUBMISSION_REVIEWER_PROGRESS_MUTATION = gql(`
  mutation ResetSubmissionReviewerProgress($submissionId: ID!, $reviewerId: ID!) {
    resetSubmissionReviewerProgress(submissionId: $submissionId, reviewerId: $reviewerId) {
      id
      status
      encampStatus
    }
  }
`);

export const MARK_SUBMISSION_REVIEWER_COMPLETE_MUTATION = gql(`
  mutation MarkSubmissionReviewerComplete($submissionId: ID!, $reviewerId: ID!) {
    markSubmissionReviewerComplete(submissionId: $submissionId, reviewerId: $reviewerId) {
      id
      status
      encampStatus
    }
  }
`);

export const BULK_CREATE_REVISION_SUBMISSIONS_MUTATION = gql(`
  mutation BulkCreateRevisionSubmissions($input: BulkCreateRevisionSubmissionsInput!) {
    bulkCreateRevisionSubmissions(input: $input) {
      succeeded
      updated
      failed
    }
  }
`);

export const BULK_CREATE_NEW_SUBMISSIONS_MUTATION = gql(`
  mutation BulkCreateNewSubmissions($input: BulkCreateNewSubmissionsInput!) {
    bulkCreateNewSubmissions(input: $input) {
      succeeded
      updated
      failed
      failedFacilityIds
    }
  }
`);

export const ASSIGN_PEER_REVIEW_TO_ME_MUTATION = gql(`
  mutation AssignPeerReviewToMe($submissionId: ID!) {
    assignPeerReviewToMe(submissionId: $submissionId) {
      id
      status
      encampStatus
    }
  }
`);
